import type { Address } from "viem"
import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import addMinutes from "date-fns/addMinutes"
import getUnixTime from "date-fns/getUnixTime"
import { observer } from "mobx-react-lite"
import { useLocalStorage } from "usehooks-ts"
import { formatUnits } from "viem"
import { useConfig } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenFalse, ShowWhenTrue } from "@/components/conditionals"
import { Separator } from "@/components/separator"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MessageBox } from "@/domains/common/components/message-box"
import { DEFAULT_SLIPPAGE, MAX_SLIPPAGE, MIN_SLIPPAGE } from "@/domains/common/config/slippage"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { DownArrowIcon, WalletIcon } from "@/domains/icons"
import { withdrawLiquidityActionInteraction } from "@/domains/interactions"
import { clampNumber, cn, trimEthAddress } from "@/domains/library"
import { LiquidityPageLayout } from "@/domains/liquidity-page/components/liquidity-page-layout"
import { LiquidityPageTabs } from "@/domains/liquidity-page/components/liquidity-page-tabs"
import { MinimalPoolInputCard } from "@/domains/minimal-pool/components/minimum-pool-input-card"
import { ClassicLiquidityPagesComplexBreadcrumbApplication } from "@/domains/navigation/components/complex-breadcrumb-applications/classic-liquidity-pages-complex-breadcrumb-application"
import { ClassicLiquiditySubItem } from "@/domains/navigation/subitems/classic-liquidity-subitem"
import { TokenIcon } from "@/domains/tokens/components/token-icon"
import { TokenInputBalance } from "@/domains/tokens/components/token-input-balance"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useMinimalPool } from "@/hooks/use-minimal-pool"
import useEventEmitter from "@/hooks/useEventEmitter"

const deadlineMinutes = 20

const BREADCRUMB_ITEMS = [new ClassicLiquiditySubItem(), ClassicLiquidityPagesComplexBreadcrumbApplication]

const UnstakePage = observer(() => {
  const config = useConfig()
  const { address: poolAddressParam } = useParams<{ address: string }>()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()
  const scaleApp = useScaleApplication()

  const poolAddress = poolAddressParam ? (poolAddressParam as Address) : undefined
  const minimalPool = useMinimalPool(poolAddress)

  // Hook up the orchestrator to run the loadUserManageMinimalPoolInfo query
  useEffect(() => {
    if (!minimalPool || !scaleApp) return
    scaleApp.MinimalPools.Orchestrator.startUserManagePoolSync(minimalPool, 30_000)
    return () => {
      scaleApp.MinimalPools.Orchestrator.stopUserManagePoolSync()
    }
  }, [minimalPool, scaleApp])

  const [amount, setAmount] = useState<string>("0")
  const amountWei = minimalPool?.parseUnits(amount) ?? 0n

  // Calculate proportional share of reserves (BigInt math)
  const token0ReceiveWei =
    minimalPool && minimalPool.onChainTotalSupply > 0n
      ? (minimalPool.onChainReserve0 * amountWei) / minimalPool.onChainTotalSupply
      : 0n

  const token1ReceiveWei =
    minimalPool && minimalPool.onChainTotalSupply > 0n
      ? (minimalPool.onChainReserve1 * amountWei) / minimalPool.onChainTotalSupply
      : 0n

  // Format for display
  const token0ReceiveDisplayValue =
    token0ReceiveWei > 0n ? formatUnits(token0ReceiveWei, minimalPool?.token0.decimals ?? 18) : "0.00"

  const token1ReceiveDisplayValue =
    token1ReceiveWei > 0n ? formatUnits(token1ReceiveWei, minimalPool?.token1.decimals ?? 18) : "0.00"

  const hasSufficientBalance = minimalPool ? amountWei <= minimalPool.onChainUserBalance : false
  const canInteract = amountWei > 0n && hasSufficientBalance

  // slippage
  // defaultSlippage may have come from local storage and is not safe,
  // lets ensure it's a valid number between MIN_SLIPPAGE and MAX_SLIPPAGE
  const [defaultSlippage, setDefaultSlippage] = useLocalStorage("swap-default-slippage", DEFAULT_SLIPPAGE)
  if (defaultSlippage <= MIN_SLIPPAGE || defaultSlippage >= MAX_SLIPPAGE) {
    setDefaultSlippage(DEFAULT_SLIPPAGE)
  }
  const [slippage, setSlippage] = useState(Number(defaultSlippage).toString())

  const deadline = getUnixTime(addMinutes(new Date(), clampNumber(deadlineMinutes, 1, 60)))

  const withdrawAction = async () => {
    if (!scaleApp.Wallet.Connection.isConnected) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }
    if (!scaleApp.Wallet.Connection.connectedAddress) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }
    if (!minimalPool || !chainConfig) {
      console.error("Missing required dependencies")
      return
    }

    const chainId = chainConfig.chainId
    const result = await withdrawLiquidityActionInteraction({
      connectedAddress: scaleApp.Wallet.Connection.connectedAddress.toViemAddress(),
      chainId,
      config,
      emitter,
      amountWei,
      minimalPool,
      routerAddress: chainConfig.equalizer.contracts.router,
      deadline,
      slippage: Number(slippage),
      onSuccess: () => {
        setAmount("0")
        scaleApp.MinimalPools.Orchestrator.userManagePoolQueryInstance?.forceExecute()
      },
    })
  }

  const title = minimalPool ? `Withdraw ${minimalPool.displayName} Liquidity` : "Withdraw Liquidity"
  const subTitle = minimalPool
    ? `to withdraw ${minimalPool?.token0.symbol} and ${minimalPool?.token1.symbol} from the LP token`
    : "to withdraw your tokens"

  return (
    <TooltipProvider>
      <PageContainer>
        <MetaData title={title} />
        <PageTitle title="Withdraw Liquidity" subTitle={subTitle} items={BREADCRUMB_ITEMS} />
        <LiquidityPageLayout minimalPool={minimalPool} page="withdraw">
          <LiquidityPageTabs />
          <Card.Container className="flex w-full min-w-[320px] max-w-xl flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 lg:p-6">
            <ShowWhenTrue when={!!minimalPool}>
              <Card.Header>
                <div className="flex w-full flex-col">
                  <Card.HeaderDescription>
                    Withdraw your {minimalPool?.displayName} back into {minimalPool?.token0.symbol} and{" "}
                    {minimalPool?.token1.symbol}
                  </Card.HeaderDescription>
                </div>
              </Card.Header>

              {minimalPool !== undefined ? (
                <MinimalPoolInputCard
                  amount={amount}
                  balance={minimalPool?.onChainUserBalance ?? 0n}
                  minimalPool={minimalPool}
                  onAmountChange={setAmount}
                  balanceSlot={
                    <TokenInputBalance token={minimalPool} label={<WalletIcon size={14} />} className="text-t200" />
                  }
                />
              ) : null}

              <div className="flex cursor-default flex-row items-center justify-center">
                <DownArrowIcon className="h-5 w-5 text-t200" />
              </div>
              <div className="flex flex-col text-t100">
                {/* token 0 */}
                <div className="flex justify-between">
                  <div className="flex flex-row items-center gap-2">
                    {minimalPool?.token0 && (
                      <>
                        <TokenIcon token={minimalPool?.token0} size={20} />
                        <span>{minimalPool?.token0.symbol}</span>
                      </>
                    )}
                  </div>
                  <div className={cn(token0ReceiveDisplayValue === "0.00" ? "text-t200" : "")}>
                    {token0ReceiveDisplayValue}
                  </div>
                </div>
                <div className="py-4">
                  <Separator
                    className="relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light
                    after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
                    after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
                    decorative
                  />
                </div>
                <div className="flex justify-between">
                  <div className="flex flex-row items-center gap-2">
                    {minimalPool?.token1 && (
                      <>
                        <TokenIcon token={minimalPool?.token1} size={20} />
                        <span>{minimalPool.token1.symbol}</span>
                      </>
                    )}
                  </div>
                  <div className={cn(token1ReceiveDisplayValue === "0.00" ? "text-t200" : "")}>
                    {token1ReceiveDisplayValue}
                  </div>
                </div>
              </div>
              <div className="my-2 flex w-full justify-end">
                <Button disabled={!canInteract} variant={"primary"} onClick={withdrawAction} size="fit">
                  Withdraw Liquidity
                </Button>
              </div>
            </ShowWhenTrue>
            <ShowWhenFalse when={!!minimalPool}>
              <MessageBox variant={"error"}>
                <p>
                  Couldn't find pair with address {trimEthAddress(poolAddress ?? "")}, click{" "}
                  <Link href={"/liquidity"} target="_self" className="underline">
                    here
                  </Link>{" "}
                  to go back to Liquidity.
                </p>
              </MessageBox>
            </ShowWhenFalse>
          </Card.Container>
        </LiquidityPageLayout>
      </PageContainer>
    </TooltipProvider>
  )
})

export default UnstakePage
